import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const CHAT_WIDGET_MIN_WIDTH = 450
// Max width is a share of the viewport rather than a fixed px value, so the sidebar can't be
// dragged wide enough to crowd out the app's main content on narrower screens.
export const CHAT_WIDGET_MAX_WIDTH_PERCENT = 0.6
export const CHAT_WIDGET_DEFAULT_WIDTH = 440

export const getChatWidgetMaxWidth = () =>
    typeof window === 'undefined' ? Infinity : window.innerWidth * CHAT_WIDGET_MAX_WIDTH_PERCENT

const clampWidth = (width: number) =>
    Math.min(getChatWidgetMaxWidth(), Math.max(CHAT_WIDGET_MIN_WIDTH, width))

interface ChatWidgetState {
    open: boolean
    toggle: () => void
    close: () => void
    // True while a view that shows its own floating chat button (e.g. report view) is mounted,
    // so the shared dashboard layout can offer both quick chat types instead of just one.
    reportViewActive: boolean
    setReportViewActive: (reportViewActive: boolean) => void
    // While reportViewActive, the single shared widget can be opened as either quick chat type:
    // the report page's own FAB opens it scoped to the open report (forceGlobal: false), while
    // the header's Insights icon opens/switches it to the global assistant (forceGlobal: true).
    // Clicking the button for the type already showing closes the widget instead of no-op'ing.
    forceGlobal: boolean
    openReport: () => void
    openGlobal: () => void
    // Type of the conversation currently shown in the widget ('report' | 'global'), published by
    // the widget so the header's Insights icon knows when it must start a fresh global chat.
    activeConversationType: 'report' | 'global'
    setActiveConversationType: (activeConversationType: 'report' | 'global') => void
    // Bumped to ask the widget to start a new conversation; the widget reacts to changes.
    newChatRequestId: number
    // User-resizable sidebar width (persisted across sessions), clamped between
    // CHAT_WIDGET_MIN_WIDTH and CHAT_WIDGET_MAX_WIDTH_PERCENT of the viewport.
    width: number
    setWidth: (width: number) => void
    isResizing: boolean
    setIsResizing: (isResizing: boolean) => void
}

export const useChatWidgetStore = create<ChatWidgetState>()(
    persist(
        (set) => ({
            open: false,
            toggle: () =>
                set((state) =>
                    state.activeConversationType === 'report'
                        ? { open: true, newChatRequestId: state.newChatRequestId + 1 }
                        : { open: !state.open }
                ),
            close: () => set({ open: false }),
            reportViewActive: false,
            setReportViewActive: (reportViewActive) => set({ reportViewActive }),
            forceGlobal: false,
            openReport: () =>
                set((state) => {
                    // Leaving global mode switches the widget's conversation type, which already
                    // resets it to a fresh report chat.
                    if (state.forceGlobal) {
                        return { open: true, forceGlobal: false }
                    }
                    // Already in report mode but showing a global conversation: start a new one.
                    if (state.activeConversationType !== 'report') {
                        return { open: true, newChatRequestId: state.newChatRequestId + 1 }
                    }
                    return state.open ? { open: false } : { open: true }
                }),
            openGlobal: () =>
                set((state) => {
                    if (state.reportViewActive && !state.forceGlobal) {
                        return { open: true, forceGlobal: true }
                    }
                    if (state.activeConversationType === 'report') {
                        return { open: true, forceGlobal: true, newChatRequestId: state.newChatRequestId + 1 }
                    }
                    return state.open && state.forceGlobal ? { open: false } : { open: true, forceGlobal: true }
                }),
            activeConversationType: 'global',
            setActiveConversationType: (activeConversationType) => set({ activeConversationType }),
            newChatRequestId: 0,
            width: CHAT_WIDGET_DEFAULT_WIDTH,
            setWidth: (width) => set({ width: clampWidth(width) }),
            isResizing: false,
            setIsResizing: (isResizing) => set({ isResizing }),
        }),
        {
            name: 'chat-widget',
            partialize: (state) => ({ width: state.width }),
        }
    )
)
